import {useState, useEffect, useRef} from 'react';
import {searchClient} from 'Callbacks/algolia/searchClient';
import SearchHeader from 'Components/SearchHeader/ies';
import getFormConfig from 'config/ies/FormConfigs/getFormConfig';
import SidebarFilters from 'Components/ProgramFinder/SidebarFilters/ies';
import ProgramFinderMeta from 'Components/ProgramFinder/ProgramFinderMeta';
import {useIntl} from 'react-intl';
import Pager from 'Components/Pager';
import scrollTo from 'utils/scrollTo';
import algoliasearchHelper from 'algoliasearch-helper';
import CardList from 'Components/ProgramFinder/CardList/ies';
import parseQueryIESProgramFinder from 'utils/parseQueryIESProgramFinder';
import fromTo from 'utils/MetaFromToIES';
import applyProgramFinderQueries from 'utils/applyProgramFinderQueries';

let timer;
let priceQuery;

const Algolia = ({maxPrice}) => {
  const cardsRef = useRef(null);
  const filterTriggeredRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const intl = useIntl();
  const {search: urlParams} = window.location;
  const params = parseQueryIESProgramFinder(urlParams);
  const {query: searchQuery, price_range, ...restQueries} = params;
  priceQuery = priceQuery ? priceQuery : price_range;
  let roundedMaxPrice = '';

  if (maxPrice) {
    const divide = maxPrice.length > 4 ? 100 : maxPrice.length > 3 ? 10 : false;
    const price = parseInt(maxPrice);
    if (divide) {
      roundedMaxPrice = Math.ceil(price / divide) * divide;
    } else {
      roundedMaxPrice = price;
    }
  }

  const {programFinderQuery, programFinderSidebar} = getFormConfig();
  const hitsPerPage = process.env.REACT_APP_ALGOLIA_DEFAULT_HITS_PER_PAGE_IES;
  const initialFacets = {
    field_prgm_type: '',
    field_prgm_options: [],
    field_prgm_disciplines: [],
    regions: '',
    destinations: [],
    field_prgm_term_type: [],
    field_prgm_lang_pre_lvl: [],
    field_prgm_lang_instruct: [],
    field_prgm_housing_opt: [],
  };
  const initialFilters = {
    price_range: {
      from: -1,
      to: roundedMaxPrice ? roundedMaxPrice : 30000,
    },
  }

  const disjunctiveFacets = [
    'field_prgm_term_type',
    'field_prgm_lang_pre_lvl'
  ];
  const facets = [
    'field_prgm_type',
    'field_prgm_options',
    'field_prgm_disciplines',
    'regions',
    'destinations',
    'field_prgm_lang_instruct',
    'field_prgm_housing_opt',
  ];
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState({
    query: searchQuery,
  });
  const [facetFilters, setFacetFilters] = useState(applyProgramFinderQueries(restQueries, initialFacets));
  const [filters, setFilters] = useState(initialFilters);
  const [filtersVisibility, setFiltersVisibility] = useState(false);
  const [priceRange, setPriceRange] = useState(`field_prgm_cost_est <= ${roundedMaxPrice ? roundedMaxPrice : '30000'}`);
  const [isLoading, setIsLoading] = useState(true);

  const search = (updatePager) => {
    if (updatePager && page !== 0) {
      // If page is not set to 0 exit search callback. On page state update search will be called again.
      setPage(0);
      return;
    }
    setIsLoading(true);
    const search = algoliasearchHelper(searchClient(), window.drupalSettings.algoliaSearch.programFinder.default, {
      query: query.query,
      hitsPerPage,
      facets,
      disjunctiveFacets,
      filters: priceRange,
    });

    // Push facets to helper.
    Object.entries(facetFilters).forEach(([key, value]) => {
      // Handle string values (radio buttons - single selection)
      if ((key === 'regions' || key === 'field_prgm_type') && typeof value === 'string' && value !== '') {
        if (facets.includes(key)) {
          search.addFacetRefinement(key, value);
        }
        return;
      }

      // Handle arrays (checkboxes and multi-selects)
      if (Array.isArray(value)) {
        value.forEach((v) => {
          let helperValue = '';
          if (v && typeof v === 'string') {
            helperValue = v;
          } else if (v && typeof v === 'object') {
            helperValue = v.value;
          }

          if (helperValue) {
            if (facets.includes(key)) {
              search.addFacetRefinement(key, helperValue);
            } else if (disjunctiveFacets.includes(key)) {
              search.addDisjunctiveFacetRefinement(key, helperValue);
            }
          }
        });
      }
    });

    search.on('result', (event) => {
      const {results} = event;
      const resultFacets = {};
      const resultDisjunctiveFacets = {};
      results.facets.forEach((facet) => {
        resultFacets[facet.name] = facet.data;
      });
      results.disjunctiveFacets.forEach((facet) => {
        resultDisjunctiveFacets[facet.name] = facet.data;
      });
      results.facets = resultFacets;
      results.disjunctiveFacets = resultDisjunctiveFacets;

      setResult(results);

      setTimeout(() => {
        setIsLoading(false);

        if (!hasInitializedRef.current) {
          hasInitializedRef.current = true;
          return;
        }

        const searchHeader = document.querySelector('.search-header');
        if (filterTriggeredRef.current && cardsRef.current) {
          cardsRef.current.focus();
          if (searchHeader) {
            searchHeader.scrollIntoView({behavior: 'smooth', block: 'start'});
          } else {
            cardsRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
          }
          filterTriggeredRef.current = false;
        }
      }, 400);
    });

    search.setPage(page).search()
  };

  useEffect(() => {
    // Keep the URL parameters on initial load.
    // Don't clear them like before.
  }, []);

  useEffect(() => {
    // Update URL with current search query and filters
    const currentUrl = new URL(window.location);

    // Clear all existing parameters
    currentUrl.search = '';

    // Add query parameter if it exists
    if (query.query) {
      currentUrl.searchParams.set('query', query.query.replaceAll(' ', '_'));
    }

    // Add facet filter parameters if they exist
    Object.entries(facetFilters).forEach(([key, values]) => {
      // Handle string values (radio buttons - single selection)
      if ((key === 'regions' || key === 'field_prgm_type') && typeof values === 'string' && values !== '') {
        currentUrl.searchParams.set(key, values.replaceAll(' ', '_'));
        return;
      }

      // Handle arrays (checkboxes and multi-selects)
      if (Array.isArray(values) && values.length > 0) {
        const facetValues = values.map(v => {
          if (typeof v === 'string') {
            return v;
          } else if (v && typeof v === 'object' && v.value) {
            return v.value;
          }
          return '';
        }).filter(v => v !== '');

        if (facetValues.length > 0) {
          currentUrl.searchParams.set(key, facetValues.join(';').replaceAll(' ', '_'));
        }
      }
    });

    window.history.replaceState({}, document.title, currentUrl.pathname + currentUrl.search);
  }, [query, facetFilters]);

  useEffect(search, [page, facetFilters, priceRange, query]);

  useEffect(() => {
    const value = `field_prgm_cost_est <= ${filters.price_range.to}`;
    clearTimeout(timer);
    timer = setTimeout(() => {
      setPriceRange(value);
    }, 1000);
  }, [filters]);

  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    }
  }, [priceRange, facetFilters]);

  if (!result) {
    return '';
  }

  const clearFacets = (event) => {
    event.preventDefault();
    filterTriggeredRef.current = true;
    // Create completely new objects with new array instances
    setFacetFilters({
      field_prgm_type: '',
      field_prgm_options: [],
      field_prgm_disciplines: [],
      regions: '',
      destinations: [],
      field_prgm_term_type: [],
      field_prgm_lang_pre_lvl: [],
      field_prgm_lang_instruct: [],
      field_prgm_housing_opt: [],
    });
    setFilters({
      price_range: {
        from: -1,
        to: roundedMaxPrice ? roundedMaxPrice : 30000,
      },
    });
    setQuery({query: ''});
  };

  const changePage = (event, newPage) => {
    event.preventDefault();
    if (newPage === page) {
      return '';
    }

    setPage(newPage);
    setTimeout(() => {
      scrollTo();
    }, 100);
  };

  return (
    <div className="program-finder mt-10">
      <SearchHeader title="ProgramFinder.Title"
                    formConfig={programFinderQuery}
                    searchCallback={search}
                    formState={query}
                    setFormState={setQuery}
                    searchButton="ProgramFinder.QuerySearch.Text"
                    setFiltersState={setFiltersVisibility}
                    indexName={window.drupalSettings.algoliaSearch.programFinder.default}/>

      <div className="lg:flex">
        <SidebarFilters facets={result.facets}
                        disjunctiveFacets={result.disjunctiveFacets}
                        formConfig={programFinderSidebar}
                        formState={facetFilters}
                        setFormState={(newFacetFilters) => {
                          filterTriggeredRef.current = true;
                          setFacetFilters(newFacetFilters);
                        }}
                        clearCallback={clearFacets}
                        filtersVisibility={filtersVisibility}
                        setFiltersVisibility={setFiltersVisibility}
                        filtersState={filters}
                        setFilters={(newFilters) => {
                          filterTriggeredRef.current = true;
                          setFilters(newFilters);
                        }}
                        maxPrice={roundedMaxPrice}
                        priceQuery={priceQuery}/>
        <div className="flex-grow" tabIndex="-1" ref={cardsRef}>
          <ProgramFinderMeta
            replaceCount={
              intl.formatMessage(
                {id: 'ProgramFinder.Meta.Text'},
                {fromTo: fromTo(result, page, hitsPerPage), nrHits: result.nbHits},
              )
            }
            resultsText="Algolia.SearchPage.ResultsMeta"/>
          {isLoading
            ?
            <div className="programs-loader" role="status" aria-live="polite" aria-busy="true">
              <div className="programs-loader-throbber">
                <span className="programs-loader-label">{intl.formatMessage({id: 'Button.Loading.Text'})}</span>
              </div>
            </div>
            : <CardList list={result.hits}/>
          }
        </div>
      </div>

      {result &&
        <Pager
          meta={{
            totalRows: result.nbHits,
            pageSize: result.hitsPerPage,
            totalPages: result.nbPages,
            pageNumber: result.page
          }}
          activePage={result.page}
          changePage={changePage}
        />
      }
    </div>
  );
}

export default Algolia;
